'use strict';

import Swiper from 'swiper';
import BaseControl from 'scripts/base-control';
import can from 'can';
import _ from 'lodash';
import appContext from 'scripts/app-context';
import DomEventsManager from 'scripts/dom-events-manager';

const THUMBS_SLIDE_SPACE = 7;

export const PhotoGallery = BaseControl.extend({
    gallery: null,
    galleryTop: null,
    galleryThumbs: null,
    previewImagesSlider: null,
    showThumbnails: false,
    isFullscreen: false,
    visible: false,

    init() {
        this._super();

        this.$galleryTopContainer = this.$element.find(
            '.swiper-container.gallery-top',
        );
        this.$loader = this.$element.find('.s-loader');
        this.$imageInfoContainer = this.$element.find('.s-image-info');
        this.$imageTitle = this.$element.find('.s-slide-title');
        this.$imageIndex = this.$element.find('.s-slide-index');
        this.$galleryThumbsWrapper = this.$element.find(
            '.gallery-thumbs-wrapper',
        );
        this.$galleryThumbsContainer = this.$element.find(
            '.swiper-container.gallery-thumbs',
        );
        this.initCollectionItems();
        this.isEmbed = this.$element.hasClass('embed');
        this.isMobile = this.options.isMobile;

        this.$galleryTopContainer
            .find('.swiper-slide')
            .each((i, el) =>
                this.$('.s-slide-desc-item--position', el).html(
                    `${i + 1} of ${this.slidesCount}`,
                ),
            );

        DomEventsManager.subscribe(this);

        if (this.$galleryTopContainer.find('.swiper-slide').length > 1) {
            this.initTopSwiper();
        } else {
            this.$element.addClass('single-slide');
            if (!this.gallery[0].image.caption) {
                this.$element.addClass('no-description');
            }
        }

        if (this.options.expanded) {
            if (
                !(
                    window.CQ &&
                    [
                        window.CQ.WCM.MODE_EDIT,
                        window.CQ.WCM.MODE_DESIGN,
                    ].indexOf(window.CQ.WCM.getMode()) > -1
                )
            ) {
                this.toggleFullscreen(true);
            }
        }

        window.addEventListener('keydown', (e) => {
            if (e.which === 27) {
                // ESC key
                this.onEscKeyPress();
            }
        });
    },

    initCollectionItems() {
        const isInfographic = this.options.infographic;
        this.gallery = isInfographic
            ? [{ image: this.options.coverImage }]
            : this.options.images;
        this.slidesCount = this.gallery.length;
    },

    // Swiper gallery initialization
    initTopSwiper() {
        const self = this;
        this.galleryTop = new Swiper(this.$galleryTopContainer, {
            spaceBetween: 0,
            preventClicks: false,
            zoom: {
                toggle: false
            },
            keyboard: {
                enabled: true,
                onlyInViewport: false,
            },
            on: {
                slideChangeTransitionEnd: function () {
                    self.onTransitionEnd(this);
                }
            },
            navigation: {
                prevEl: this.$galleryTopContainer.find('.swiper-button-prev'),
                nextEl: this.$galleryTopContainer.find('.swiper-button-next'),
            },
        });

        if (!this.isEmbed) {
            can.route.bind('change', function(ev, attr, how, newVal, oldVal) {
                if (attr == 'slide') {
                    self.setActiveSlide(parseInt(newVal));
                }
            });

            can.route.ready();
        }
    },

    initThumbsSwiper() {
        this.galleryThumbs = new Swiper(this.$galleryThumbsContainer, {
            spaceBetween: THUMBS_SLIDE_SPACE,
            slidesPerView: 'auto',
            slidesPerGroup: 2,
            navigation: {
                prevEl: this.$galleryThumbsWrapper.find('.swiper-button-prev'),
                nextEl: this.$galleryThumbsWrapper.find('.swiper-button-next'),
            },
            touchRatio: 0.2,
            freeMode: true,
            slideToClickedSlide: false,
        });

        this.updateGalleryThumbsSlides(this.galleryTop.activeIndex);
    },

    onTransitionEnd(slider) {
        let activeIndex = slider.activeIndex;

        if (this.galleryThumbs) {
            this.galleryThumbs.slideTo(activeIndex);
        }
        this.updateGalleryThumbsSlides(activeIndex);
        this.setActiveSlide(activeIndex);
    },

    updateGalleryThumbsSlides(activeIndex) {
        let index = parseInt(activeIndex);

        index = isNaN(index) ? 0 : index;

        this.$galleryThumbsContainer
            .find('.expanded-slide')
            .removeClass('expanded-slide');
        this.$galleryThumbsContainer
            .find('.swiper-slide')
            .eq(index)
            .addClass('expanded-slide');
    },

    setActiveSlide(slide) {
        let index = 0;

        if (!_.isNaN(slide) && slide < this.slidesCount) {
            index = slide;
        }

        if (!this.isEmbed) {
            can.route.attr({ slide: index });
        }

        let imageData = this.gallery[index];

        if (!imageData) {
            return;
        }

        let imageCaption = imageData.image.caption || '';
        if (imageCaption.length > 260) {
            imageCaption = imageCaption.substring(0, 260) + '...';
        }
        this.$imageTitle.text(imageCaption);
        this.$imageIndex.text(`${index + 1} of ${this.slidesCount}`);
        if (this.slidesCount == 1) {
            this.$imageIndex.addClass('single-slide');
        }

        if (this.galleryTop) {
            this.galleryTop.slideTo(index);
        }
        if (this.galleryThumbs) {
            this.galleryThumbs.slideTo(index);
        }
    },

    thumbnailsToggle(stateOverride) {
        this.showThumbnails =
            stateOverride !== undefined ? stateOverride : !this.showThumbnails;
        this.$element.toggleClass('thumbnails-shown', this.showThumbnails);
        this.showThumbnails
            ? this.$galleryThumbsWrapper.slideDown({
                  duration: 150,
                  easing: 'linear',
              })
            : this.$galleryThumbsWrapper.slideUp({
                  duration: 150,
                  easing: 'linear',
              });
        this.updateThumbsSlider();
        this.$galleryThumbsWrapper
            .find('image-cloudinary')
            .each((i, component) => {
                this.$(component)
                    .viewModel()
                    .recalculateSize(component);
                this.$(component)
                    .viewModel()
                    .show();
            });
    },

    getCurrentSlideIndex() {
        let slide = this.galleryTop
            ? this.galleryTop.activeIndex
            : this.isEmbed
            ? parseInt(can.route.attr('slide', 0))
            : 0;

        if (isNaN(slide)) {
            slide = 0;
        }

        return slide;
    },

    updateSliders() {
        setTimeout(
            function() {
                this.updateSlider();
                this.updateThumbsSlider();
            }.bind(this),
            250,
        );
    },

    updateSlider() {
        const slide = this.getCurrentSlideIndex();

        if (this.galleryTop) {
            this.galleryTop.detachEvents();
            this.galleryTop.destroy();
            this.galleryTop = null;
        }
        this.initTopSwiper();
        this.setActiveSlide(slide);
    },

    updateThumbsSlider() {
        const slide = this.getCurrentSlideIndex();

        if (this.galleryThumbs) {
            this.galleryThumbs.detachEvents();
            this.galleryThumbs.destroy();
            this.galleryThumbs = null;
        }

        if (this.checkThumbsFitness()) {
            this.$galleryThumbsWrapper.addClass('thumbs-fit');
        } else {
            this.$galleryThumbsWrapper.removeClass('thumbs-fit');
            this.initThumbsSwiper();
        }

        this.updateGalleryThumbsSlides(slide);

        if (this.galleryThumbs) {
            this.galleryThumbs.slideTo(slide);
        }
    },

    checkThumbsFitness() {
        const thumbsWidth = this.$galleryThumbsContainer
            .find('.swiper-slide')
            .toArray()
            .reduce(
                (prev, el) =>
                    prev +
                    this.$(el).width() +
                    (prev > 0 ? THUMBS_SLIDE_SPACE : 0),
                0,
            );

        return thumbsWidth <= this.$galleryThumbsContainer.width();
    },

    toggleFullscreen(stateOverride) {
        let that = this;

        if (this.isMobile) {
            return;
        }
        this.isFullscreen =
            stateOverride !== undefined ? stateOverride : !this.isFullscreen;
        appContext.model.dispatch('page-scroll-toggle', [this.isFullscreen]);
        this.$element.toggleClass('is-full-screen', this.isFullscreen);

        setTimeout(function() {
            that.$loader.fadeOut('slow');
        }, 500);

        if (
            this.isEmbed &&
            ['small', 'extra-small'].indexOf(
                appContext.model.screen.attr('screenSize'),
            ) > -1
        ) {
            this.thumbnailsToggle(false);
        }

        this.updateSliders();

        if (this.isFullscreen) {
            setTimeout(function() {
                that.$galleryTopContainer
                    .find('image-cloudinary')
                    .each(function(i, component) {
                        that.$(component)
                            .viewModel()
                            .recalculateSize(component);
                        that.$(component)
                            .viewModel()
                            .show();
                    });
            }, 250);
        }
    },

    onFirstShow() {
        this.visible = true;
        this.updateSliders();
    },

    onWindowResize() {
        this.updateSliders();
    },

    onEscKeyPress() {
        if (this.isFullscreen) {
            this.$loader.show();
            this.toggleFullscreen();
        }
    },

    '.s-gallery-short  click'() {
        this.$loader.show();
        this.toggleFullscreen();
    },

    '.js-fullscreen click'() {
        this.$loader.show();
        this.toggleFullscreen();
    },

    '.js-thumbs click'() {
        this.thumbnailsToggle();
    },

    '.swiper-container.gallery-thumbs .swiper-slide click'($el) {
        let index = $el.data('slide-id');
        this.setActiveSlide(index);
    },

    '.swiper-container.gallery-top .swiper-slide, .s-image-info click'() {
        this.$element.toggleClass('is-hidden-description');
        this.$element.hasClass('is-hidden-description')
            ? this.$imageInfoContainer.fadeOut({
                  duration: 150,
                  easing: 'linear',
              })
            : this.$imageInfoContainer.fadeIn({
                  duration: 150,
                  easing: 'linear',
              });
    },
});
